import React, { useState, useEffect } from 'react';

const MatchGame = ({ flashcard, terms, onExit }) => {
  const [gameState, setGameState] = useState('playing'); // 'playing' or 'completed'
  const [cards, setCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [animatingPairs, setAnimatingPairs] = useState([]);
  const [incorrectAttempts, setIncorrectAttempts] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [finalTime, setFinalTime] = useState(0);

  useEffect(() => {
    initializeGame();
  }, [terms]);

  useEffect(() => {
    if (gameState === 'playing' && startTime) {
      const interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 100);
      return () => clearInterval(interval);
    }
  }, [gameState, startTime]);

  const initializeGame = () => {
    if (!terms || terms.length === 0) return;

    // Create cards: one for term, one for definition
    const gameCards = [];
    terms.forEach((term, index) => {
      gameCards.push({
        id: `term-${index}`,
        content: term.term,
        pairId: index,
        type: 'term'
      });
      gameCards.push({
        id: `def-${index}`,
        content: term.definition,
        pairId: index,
        type: 'definition'
      });
    });

    // Shuffle cards
    const shuffled = gameCards.sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setStartTime(Date.now());
    setGameState('playing');
    setMatchedPairs([]);
    setAnimatingPairs([]);
    setSelectedCards([]);
    setIncorrectAttempts(0);
    setElapsedTime(0);
  };

  const handleCardClick = (card) => {
    if (gameState !== 'playing') return;
    if (matchedPairs.includes(card.pairId)) return;
    if (animatingPairs.includes(card.pairId)) return;
    if (selectedCards.find(c => c.id === card.id)) return;
    if (selectedCards.length >= 2) return;

    const newSelected = [...selectedCards, card];
    setSelectedCards(newSelected);

    if (newSelected.length === 2) {
      // Check if it's a match
      const [first, second] = newSelected;
      if (first.pairId === second.pairId && first.type !== second.type) {
        // Correct match! Add to animating state
        setAnimatingPairs([...animatingPairs, first.pairId]);
        
        setTimeout(() => {
          setMatchedPairs([...matchedPairs, first.pairId]);
          setAnimatingPairs(prev => prev.filter(id => id !== first.pairId));
          setSelectedCards([]);
          
          // Check if game is complete
          if (matchedPairs.length + 1 === terms.length) {
            const finalTimeSeconds = Math.floor((Date.now() - startTime) / 1000);
            setFinalTime(finalTimeSeconds);
            setGameState('completed');
          }
        }, 800);
      } else {
        // Incorrect match
        setIncorrectAttempts(prev => prev + 1);
        setTimeout(() => {
          setSelectedCards([]);
        }, 800);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTotalTime = () => {
    const baseTime = finalTime || elapsedTime;
    const penalty = incorrectAttempts * 2; // 2 seconds penalty per incorrect match
    return baseTime + penalty;
  };

  const isCardMatched = (card) => matchedPairs.includes(card.pairId);
  const isCardAnimating = (card) => animatingPairs.includes(card.pairId);
  const isCardSelected = (card) => selectedCards.find(c => c.id === card.id);
  const isCardIncorrect = (card) => {
    if (selectedCards.length === 2) {
      const [first, second] = selectedCards;
      if ((first.id === card.id || second.id === card.id) && 
          (first.pairId !== second.pairId || first.type === second.type)) {
        return true;
      }
    }
    return false;
  };

  if (gameState === 'completed') {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          border: '2px solid #48bb78'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ 
            color: '#48bb78', 
            fontSize: '28px', 
            marginBottom: '12px',
            fontWeight: '700'
          }}>
            Congratulations!
          </h2>
          <p style={{ color: '#718096', fontSize: '16px', marginBottom: '24px' }}>
            You matched all {terms.length} pairs!
          </p>

          <div style={{
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '16px',
              marginBottom: '16px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
                  Base Time
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
                  {formatTime(finalTime)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#718096', marginBottom: '4px' }}>
                  Incorrect Matches
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#f56565' }}>
                  {incorrectAttempts}
                </div>
              </div>
            </div>
            {incorrectAttempts > 0 && (
              <div style={{
                fontSize: '13px',
                color: '#718096',
                marginBottom: '12px',
                paddingTop: '12px',
                borderTop: '1px solid #e2e8f0'
              }}>
                Time Penalty: +{incorrectAttempts * 2} seconds ({incorrectAttempts} × 2s)
              </div>
            )}
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#667eea',
              paddingTop: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              Final Time: {formatTime(getTotalTime())}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={initializeGame}
              style={{
                padding: '12px 24px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5568d3'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
            >
              🔄 Play Again
            </button>
            <button
              onClick={onExit}
              style={{
                padding: '12px 24px',
                backgroundColor: '#e2e8f0',
                color: '#4a5568',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#cbd5e0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
            >
              Exit Game
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <style>{`
        @keyframes matchSuccess {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          25% {
            transform: scale(1.1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05) rotate(5deg);
            opacity: 1;
          }
          75% {
            transform: scale(1.1) rotate(-5deg);
            opacity: 0.8;
          }
          100% {
            transform: scale(0);
            opacity: 0;
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
      `}</style>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px',
        padding: '16px 20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
            Match Game - {flashcard.title}
          </h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            Match {terms.length} terms with their definitions
          </p>
        </div>
        <button 
          onClick={onExit}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f56565',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e53e3e'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f56565'}
        >
          Exit Game
        </button>
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        padding: '12px 16px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
            TIME
          </div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#667eea' }}>
            {formatTime(elapsedTime)}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
            MATCHED
          </div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#48bb78' }}>
            {matchedPairs.length} / {terms.length}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
            INCORRECT
          </div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#f56565' }}>
            {incorrectAttempts}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
            PENALTY
          </div>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#ed8936' }}>
            +{incorrectAttempts * 2}s
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#edf2f7',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #cbd5e0'
      }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#4a5568' }}>
          💡 <strong>How to play:</strong> Click on a term, then click on its matching definition. 
          Match all pairs as quickly as possible. Each incorrect match adds 2 seconds to your time!
        </p>
      </div>

      {/* Game Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '12px',
        padding: '20px',
        backgroundColor: '#f7fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        minHeight: '400px'
      }}>
        {cards.map(card => {
          const matched = isCardMatched(card);
          const animating = isCardAnimating(card);
          const selected = isCardSelected(card);
          const incorrect = isCardIncorrect(card);
          
          // Don't render matched cards (but show animating ones)
          if (matched && !animating) return null;
          
          return (
            <div
              key={card.id}
              onClick={() => !animating && handleCardClick(card)}
              style={{
                padding: '20px',
                backgroundColor: animating ? '#c6f6d5' : selected ? (incorrect ? '#fed7d7' : '#e6f2ff') : 'white',
                border: animating ? '2px solid #48bb78' : selected ? (incorrect ? '2px solid #f56565' : '2px solid #667eea') : '2px solid #e2e8f0',
                borderRadius: '8px',
                cursor: animating ? 'default' : 'pointer',
                transition: 'all 0.2s',
                minHeight: '100px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                transform: selected && !incorrect && !animating ? 'scale(0.98)' : 'scale(1)',
                boxShadow: selected && !animating ? '0 4px 12px rgba(102, 126, 234, 0.2)' : animating ? '0 8px 24px rgba(72, 187, 120, 0.4)' : 'none',
                animation: animating ? 'matchSuccess 0.8s ease-out forwards' : incorrect ? 'shake 0.5s ease-in-out' : 'none'
              }}
              onMouseEnter={(e) => {
                if (!animating) {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!animating) {
                  e.currentTarget.style.boxShadow = selected ? '0 4px 12px rgba(102, 126, 234, 0.2)' : 'none';
                  e.currentTarget.style.transform = selected ? 'scale(0.98)' : 'scale(1)';
                }
              }}
            >
              {animating && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  fontSize: '24px',
                  animation: 'matchSuccess 0.8s ease-out'
                }}>
                  ✓
                </div>
              )}
              <div style={{
                fontSize: '10px',
                fontWeight: '600',
                color: animating ? '#2f855a' : card.type === 'term' ? '#667eea' : '#48bb78',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '8px'
              }}>
                {card.type === 'term' ? 'Term' : 'Definition'}
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: animating ? '#2f855a' : '#2d3748',
                lineHeight: '1.4',
                wordBreak: 'break-word'
              }}>
                {card.content}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MatchGame;
